import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { staffReportTemplateSchema } from '~/hooks/queries/useStaffReportTemplatesQuery/staffReportTemplateSchema';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';

const assignabilitySchema = z.object({
  hasAccountLimit: z.boolean(),
  alreadyAssigned: z.boolean(),
  canAssign: z.boolean(),
});

const staffClientReportTemplateSchema = staffReportTemplateSchema.extend({
  assignability: assignabilitySchema,
});

const staffClientReportTemplateListSchema = z.array(staffClientReportTemplateSchema);

export type StaffClientReportTemplate = z.infer<typeof staffClientReportTemplateSchema>;
export type StaffClientReportTemplatesResponse = { templates: StaffClientReportTemplate[] };

function parseStaffClientReportTemplatesList(data: unknown): StaffClientReportTemplate[] {
  if (Array.isArray(data)) {
    return staffClientReportTemplateListSchema.parse(data);
  }
  return z.object({ templates: staffClientReportTemplateListSchema }).parse(data).templates;
}

const fetchStaffClientReportTemplates = async (
  accountType: 'client' | 'partner',
  accountId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
): Promise<StaffClientReportTemplatesResponse> => {
  const { data } = await customFetch.get<unknown>(
    `/authenticated/staff/${accountType}/${accountId}/analytics/report-templates`,
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(analyticsReportingMode, statKey ? { statKey } : undefined),
    },
  );
  return { templates: parseStaffClientReportTemplatesList(data) };
};

export const staffClientReportTemplatesAccountQueryKey = (
  accountType: 'client' | 'partner',
  accountId: string,
) => ['staff', 'client-report-templates', accountType, accountId] as const;

export const getStaffClientReportTemplatesQueryOptions = (
  accountType: 'client' | 'partner',
  accountId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  queryOptions({
    queryKey: [
      ...staffClientReportTemplatesAccountQueryKey(accountType, accountId),
      statKey,
      staffConfigModeQueryKeyPart(analyticsReportingMode),
    ],
    queryFn: () =>
      fetchStaffClientReportTemplates(accountType, accountId, statKey, analyticsReportingMode),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStaffClientReportTemplatesQuery = (
  accountType: 'client' | 'partner',
  accountId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  useQuery({
    ...getStaffClientReportTemplatesQueryOptions(
      accountType,
      accountId,
      statKey,
      analyticsReportingMode,
    ),
    enabled: !!accountId && !!accountType,
  });
